/**
 * Shared DatePicker for all date-entry flows across the app.
 *
 * Use this instead of `<Input type="date" />` or any other raw/native picker.
 * Values are ISO date strings ("YYYY-MM-DD"). `null` means no date.
 */
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseISO(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, y, m, d] = match;
  if (!y || !m || !d) return null;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
}

function toISO(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function formatDisplay(value: string | null | undefined): string {
  const date = parseISO(value);
  if (!date) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isDisabled(date: Date, min: Date | null, max: Date | null): boolean {
  if (min && date < min) return true;
  if (max && date > max) return true;
  return false;
}

function buildMonth(year: number, month: number): Date[] {
  // 7 columns (Sun..Sat), enough rows to cover the full month (6 rows).
  const firstOfMonth = new Date(year, month, 1);
  const leadingEmpty = firstOfMonth.getDay();
  const start = new Date(year, month, 1 - leadingEmpty);
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    cells.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return cells;
}

export interface DatePickerProps {
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  /** Inclusive max date (ISO). */
  max?: string | null;
  /** Inclusive min date (ISO). */
  min?: string | null;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  /** Extra classes for the trigger button. */
  className?: string;
  /** Compact trigger used in dense rows/tables. */
  size?: "default" | "sm";
  /** Accessible label for the trigger. */
  ariaLabel?: string;
  /** Controls open state externally. Useful for inline-edit parents. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Auto-open on mount — useful when the picker replaces an inline-edit field. */
  autoOpen?: boolean;
}

export function DatePicker({
  value,
  onChange,
  max,
  min,
  placeholder = "Select date",
  disabled,
  clearable = true,
  className,
  size = "default",
  ariaLabel,
  open: controlledOpen,
  onOpenChange,
  autoOpen,
}: DatePickerProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(!!autoOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const selected = parseISO(value ?? null);
  const minDate = parseISO(min ?? null);
  const maxDate = parseISO(max ?? null);

  const [viewYear, setViewYear] = useState(() => (selected ?? new Date()).getFullYear());
  const [viewMonth, setViewMonth] = useState(() => (selected ?? new Date()).getMonth());

  // Snap the month view to the selected date each time the picker opens.
  const prevOpen = useRef(open);
  if (open && !prevOpen.current) {
    const base = selected ?? new Date();
    if (base.getFullYear() !== viewYear) setViewYear(base.getFullYear());
    if (base.getMonth() !== viewMonth) setViewMonth(base.getMonth());
  }
  prevOpen.current = open;

  const cells = useMemo(() => buildMonth(viewYear, viewMonth), [viewYear, viewMonth]);
  const today = new Date();

  function shiftMonth(delta: number) {
    const next = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(next.getFullYear());
    setViewMonth(next.getMonth());
  }

  function selectDate(date: Date) {
    if (isDisabled(date, minDate, maxDate)) return;
    onChange(toISO(date));
    setOpen(false);
  }

  function goToday() {
    const iso = todayISO();
    const d = parseISO(iso);
    if (!d) return;
    if (isDisabled(d, minDate, maxDate)) return;
    onChange(iso);
    setOpen(false);
  }

  const triggerSizeClass =
    size === "sm" ? "h-8 px-2 text-sm min-w-[9rem]" : "h-9 px-3 text-sm min-w-[11rem]";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel ?? placeholder}
          className={cn(
            "inline-flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-white text-left text-gray-900 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#334155]/30 disabled:cursor-not-allowed disabled:opacity-50",
            triggerSizeClass,
            !value && "text-gray-400",
            className,
          )}
        >
          <span className="flex items-center gap-2 truncate">
            <Calendar className="h-3.5 w-3.5 shrink-0 text-gray-400" />
            <span className="truncate">{value ? formatDisplay(value) : placeholder}</span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto rounded-none border border-gray-300 p-0 shadow-lg"
        align="start"
      >
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-2 py-2">
          <button
            type="button"
            onClick={() => shiftMonth(-1)}
            aria-label="Previous month"
            className="inline-flex h-7 w-7 items-center justify-center text-gray-600 hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#334155]/30"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="text-sm font-semibold text-gray-900">
            {MONTHS[viewMonth]} {viewYear}
          </div>
          <button
            type="button"
            onClick={() => shiftMonth(1)}
            aria-label="Next month"
            className="inline-flex h-7 w-7 items-center justify-center text-gray-600 hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#334155]/30"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <div className="px-2 pt-2">
          <div
            className="grid text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500"
            style={{ gridTemplateColumns: "repeat(7, 2.25rem)" }}
          >
            {WEEKDAYS.map((w) => (
              <div key={w} className="py-1.5">
                {w}
              </div>
            ))}
          </div>

          <div
            className="grid border-t border-l border-gray-200"
            style={{ gridTemplateColumns: "repeat(7, 2.25rem)" }}
          >
            {cells.map((d) => {
              const outside = d.getMonth() !== viewMonth;
              const disabledCell = isDisabled(d, minDate, maxDate);
              const isSelected = selected ? sameDay(d, selected) : false;
              const isToday = sameDay(d, today);
              return (
                <button
                  key={toISO(d)}
                  type="button"
                  disabled={disabledCell}
                  onClick={() => selectDate(d)}
                  className={cn(
                    "h-9 w-9 border-r border-b border-gray-200 text-sm tabular-nums transition-colors",
                    "hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#334155]/40",
                    outside ? "text-gray-300" : "text-gray-800",
                    isToday && !isSelected && "font-semibold text-[#334155]",
                    isSelected &&
                      "bg-[#334155] text-white hover:bg-[#415A77] focus-visible:ring-white/40",
                    disabledCell && "cursor-not-allowed bg-gray-50 text-gray-300 hover:bg-gray-50",
                  )}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-1 flex items-center justify-between gap-2 border-t border-gray-200 bg-gray-50 px-2 py-1.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={goToday}
            className="h-7 rounded-none text-xs font-medium"
          >
            Today
          </Button>
          {clearable && value ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className="h-7 rounded-none text-xs text-gray-500 hover:text-gray-700"
            >
              Clear
            </Button>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}
